import { useSyncExternalStore, type ReactNode } from "react";
import "./tips.css";

/**
 * First-time tips: one short, dismissible card per screen, shown only until the person dismisses it (or turns tips off).
 * Browser-local on purpose: tips are a per-viewer convenience, and the screen works the same without them.
 */
const KEY = "celia.tips";
type State = { off: boolean; dismissed: string[] };
const listeners = new Set<() => void>();
const SERVER: State = { off: true, dismissed: [] };
let cache: State | undefined;

function read(): State {
  if (cache) return cache;
  try {
    const value = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<State> | null;
    cache = { off: Boolean(value?.off), dismissed: Array.isArray(value?.dismissed) ? value!.dismissed.filter((x) => typeof x === "string") : [] };
  } catch { cache = { off: false, dismissed: [] }; }
  return cache;
}

function write(next: State) {
  cache = next;
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* Storage is optional; the choice still holds for this page. */ }
  listeners.forEach((listener) => listener());
}

const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };

export function useTipsEnabled() {
  return !useSyncExternalStore(subscribe, () => read().off, () => true);
}

/** Turn tips back on and show every tip again (the account menu's "Show tips"). */
export function resetTips() { write({ off: false, dismissed: [] }); }
export function hideTips() { write({ ...read(), off: true }); }

export function Tip({ id, title, when = true, children }: { id: string; title: string; when?: boolean; children: ReactNode }) {
  const state = useSyncExternalStore(subscribe, read, () => SERVER);
  if (!when || state.off || state.dismissed.includes(id)) return null;
  return <aside className="tip-card" role="note" aria-label={`Tip: ${title}`} data-testid={`tip-${id}`}>
    <span className="tip-mark" aria-hidden="true">?</span>
    <div className="tip-copy"><strong>{title}</strong><div>{children}</div></div>
    <div className="tip-actions">
      <button type="button" onClick={() => write({ ...read(), dismissed: [...read().dismissed, id] })}>Got it</button>
      <button type="button" className="is-quiet" onClick={hideTips}>Hide all tips</button>
    </div>
  </aside>;
}
